import React, { useState } from 'react';
import { SEGMENT_NAMES } from '../../scoring';
import { TEAM_COLORS } from './PieChart';

const styles = {
    container: { marginTop: '20px' },
    teamSelector: {
        display: 'flex', flexWrap: 'wrap', justifyContent: 'center',
        gap: '10px', marginBottom: '30px',
    },
    teamBtn: (active, idx) => ({
        padding: '10px 20px',
        backgroundColor: active ? TEAM_COLORS[idx % TEAM_COLORS.length] : '#e0e0e0',
        color: active ? '#fff' : '#333',
        border: 'none', borderRadius: '8px', cursor: 'pointer',
        fontSize: '14px', fontWeight: 'bold', minWidth: '100px',
    }),
    sectionTitle: {
        fontSize: '16px', fontWeight: 'bold', color: '#4b2e83',
        marginBottom: '10px', marginTop: '20px',
    },
    table: {
        width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginBottom: '20px',
    },
    th: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '10px 8px',
        textAlign: 'right', borderBottom: '2px solid #ddd',
    },
    thLeft: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '10px 8px',
        textAlign: 'left', borderBottom: '2px solid #ddd',
    },
    td: {
        padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee',
    },
    tdLeft: {
        padding: '8px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 'bold',
    },
    noSelection: {
        textAlign: 'center', color: '#999', padding: '40px', fontStyle: 'italic', fontSize: '16px',
    },
};

const fmt = (val) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const IndividualTeamResults = ({ scores, teams, rounds: roundsProp }) => {
    const teamNames = teams.map(t => t.teamName).sort();
    const [selectedTeam, setSelectedTeam] = useState(null);
    const rounds = roundsProp || [1, 2, 3];

    const getTeamRoundData = (teamName, round) => {
        const roundScores = scores[`round${round}`];
        if (!roundScores || !roundScores[teamName]) return null;
        return roundScores[teamName];
    };

    // Summary data
    const summaryRows = selectedTeam ? rounds.map(r => {
        const data = getTeamRoundData(selectedTeam, r);
        return {
            round: r,
            profit: data?.totalProfit || 0,
            units: data?.totalUnits || 0,
        };
    }) : [];

    const totalProfit = summaryRows.reduce((s, r) => s + r.profit, 0);
    const totalUnits = summaryRows.reduce((s, r) => s + r.units, 0);

    // Segment breakdown
    const segmentData = selectedTeam ? SEGMENT_NAMES.map(seg => {
        const perRound = rounds.map(r => {
            const data = getTeamRoundData(selectedTeam, r);
            return data?.segmentResults?.[seg]?.units || 0;
        });
        const total = perRound.reduce((s, v) => s + v, 0);
        return { segment: seg, perRound, total };
    }) : [];

    return (
        <div style={styles.container}>
            {/* Team selector buttons */}
            <div style={styles.teamSelector}>
                {teamNames.map((name, idx) => (
                    <button
                        key={name}
                        style={styles.teamBtn(selectedTeam === name, idx)}
                        onClick={() => setSelectedTeam(name)}
                    >
                        {name}
                    </button>
                ))}
            </div>

            {!selectedTeam ? (
                <p style={styles.noSelection}>Select a team above to view their results.</p>
            ) : (
                <>
                    <h2 style={{ textAlign: 'center', color: '#333', marginBottom: '20px' }}>{selectedTeam}</h2>

                    {/* Summary table: Profit + Units per round */}
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.thLeft}></th>
                                {rounds.map(r => (
                                    <th key={r} style={styles.th}>Round {r}</th>
                                ))}
                                <th style={{ ...styles.th, fontWeight: 'bold' }}>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={styles.tdLeft}>Profit</td>
                                {summaryRows.map((row, i) => (
                                    <td key={i} style={styles.td}>{fmt(row.profit)}</td>
                                ))}
                                <td style={{ ...styles.td, fontWeight: 'bold' }}>{fmt(totalProfit)}</td>
                            </tr>
                            <tr>
                                <td style={styles.tdLeft}>Unit Sales</td>
                                {summaryRows.map((row, i) => (
                                    <td key={i} style={styles.td}>{row.units.toLocaleString()}</td>
                                ))}
                                <td style={{ ...styles.td, fontWeight: 'bold' }}>{totalUnits.toLocaleString()}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Segment breakdown table */}
                    <div style={styles.sectionTitle}>Unit Sales by Segment</div>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.thLeft}>Audience</th>
                                {rounds.map(r => (
                                    <th key={r} style={styles.th}>Unit Sales</th>
                                ))}
                                <th style={styles.th}>Total</th>
                            </tr>
                            <tr>
                                <th style={styles.thLeft}></th>
                                {rounds.map(r => (
                                    <th key={r} style={{ ...styles.th, fontSize: '11px', fontWeight: 'normal' }}>Round {r}</th>
                                ))}
                                <th style={styles.th}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {segmentData.map(seg => (
                                <tr key={seg.segment}>
                                    <td style={styles.tdLeft}>{seg.segment}</td>
                                    {seg.perRound.map((val, i) => (
                                        <td key={i} style={styles.td}>
                                            {val > 0 ? val.toLocaleString() : '-'}
                                        </td>
                                    ))}
                                    <td style={{ ...styles.td, fontWeight: 'bold' }}>
                                        {seg.total > 0 ? seg.total.toLocaleString() : '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
        </div>
    );
};

export default IndividualTeamResults;
